import { ChordLib, getDiatonicFunction } from '@tunasong/music-lib'
import type { Chord, Substitution } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { ReharmSlot } from './reharm-slot.js'
import styles from './reharm-progression-strip.module.css'

export interface ActiveSubstitution {
  slotIndex: number
  substitution: Substitution
}

export interface ReharmProgressionStripProps {
  progression: readonly Chord[]
  keyScale: Scale
  activeSubstitution: ActiveSubstitution | null
  selectedSlotIndex: number | null
  onSlotClick: (index: number) => void
}

interface ResolvedSlot {
  chord: Chord
  roman: string
}

const resolveSlot = (
  i: number,
  chord: Chord,
  keyScale: Scale,
  activeSubstitution: ActiveSubstitution | null
): ResolvedSlot => {
  if (activeSubstitution) {
    if (activeSubstitution.slotIndex === i && activeSubstitution.substitution.kind === 'replace') {
      return { chord: activeSubstitution.substitution.chord, roman: activeSubstitution.substitution.romanLabel }
    }
    if (
      activeSubstitution.slotIndex === i - 1 &&
      activeSubstitution.substitution.kind === 'replace-next'
    ) {
      return { chord: activeSubstitution.substitution.chord, roman: activeSubstitution.substitution.romanLabel }
    }
  }
  const diatonic = getDiatonicFunction(chord, keyScale)
  return { chord, roman: diatonic ?? '' }
}

const isSlotActive = (i: number, active: ActiveSubstitution | null): boolean => {
  if (!active) {return false}
  if (active.slotIndex === i) {return true}
  if (active.substitution.kind === 'replace-next' && active.slotIndex === i - 1) {return true}
  return false
}

/**
 * Renders the 4 (or 5 when an insert-before substitution is active) slot
 * chips. The active substitution drives per-slot rendering: a `replace`
 * substitutes the chord shown at the matching slot; a `replace-next` swaps
 * the chord at `slotIndex + 1`; an `insert-before` adds a dashed-bordered
 * chip LEFT of the target slot (Bowie §8.4).
 */
export const ReharmProgressionStrip: FC<ReharmProgressionStripProps> = props => {
  const { progression, keyScale, activeSubstitution, selectedSlotIndex, onSlotClick } = props

  const insertedBefore =
    activeSubstitution && activeSubstitution.substitution.kind === 'insert-before'
      ? activeSubstitution.slotIndex
      : null

  return (
    <div className={styles.strip} role="group" aria-label="Progression slots">
      {progression.map((chord, i) => {
        const slot = resolveSlot(i, chord, keyScale, activeSubstitution)
        return (
          <span key={i} className={styles.slotGroup}>
            {insertedBefore === i && activeSubstitution ? (
              <InsertedChip chord={activeSubstitution.substitution.chord} onClick={() => onSlotClick(i)} />
            ) : null}
            <ReharmSlot
              chord={slot.chord}
              index={i}
              romanLabel={slot.roman}
              selected={selectedSlotIndex === i}
              active={isSlotActive(i, activeSubstitution)}
              onClick={onSlotClick}
            />
          </span>
        )
      })}
    </div>
  )
}

interface InsertedChipProps {
  chord: Chord
  onClick: () => void
}

const InsertedChip: FC<InsertedChipProps> = props => {
  const { chord, onClick } = props
  return (
    <button
      type="button"
      className={styles.insertedChip}
      aria-label={`Inserted chord ${ChordLib.getName(chord)}`}
      onClick={onClick}
    >
      <span className={styles.insertedTag}>+ inserted</span>
      <span className={styles.insertedName}>{ChordLib.getName(chord)}</span>
    </button>
  )
}
